import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { company } from '../mock';
import { submitContact } from '../lib/api';

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Please fill required fields', description: 'Name, email and message are required.' });
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast({ title: 'Message sent!', description: "Thanks for reaching out. We'll respond within 24 hours." });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again in a moment.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading font-bold text-slate-900 text-3xl md:text-4xl">Get in Touch</h2>
          <p className="mt-3 text-slate-500 text-lg">Have a question? We're here to help!</p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-10">
          {/* Form */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-100">
            <h3 className="font-heading font-semibold text-slate-900 text-xl">Send Us a Message</h3>
            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="John Doe" className="bg-white h-11" />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="you@example.com" className="bg-white h-11" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input id="phone" name="phone" value={form.phone} onChange={handleChange} placeholder="(905) 909-7555" className="bg-white h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="How can we help you?" rows={5} className="bg-white resize-none" />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg">
                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending...</> : <>Send Message <Send size={16} className="ml-1.5" /></>}
              </Button>
            </form>
          </div>

          {/* Info */}
          <div className="space-y-5">
            <h3 className="font-heading font-semibold text-slate-900 text-xl">Contact Information</h3>

            <div className="flex items-start gap-4 bg-white border border-slate-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Phone size={20} /></div>
              <div>
                <div className="font-medium text-slate-900">Phone Support</div>
                <a href={company.phoneHref} className="text-blue-600 hover:underline">{company.phone}</a>
                <div className="text-slate-400 text-sm mt-0.5">24/7 Technical Support</div>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white border border-slate-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center shrink-0"><Mail size={20} /></div>
              <div>
                <div className="font-medium text-slate-900">Email</div>
                <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline break-all">{company.email}</a>
                <div className="text-slate-400 text-sm mt-0.5">We respond within 24 hours</div>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-white border border-slate-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><MapPin size={20} /></div>
              <div>
                <div className="font-medium text-slate-900">Office Location</div>
                <div className="text-slate-600 text-sm mt-1 leading-relaxed">
                  {company.address.line1}<br />
                  {company.address.city}<br />
                  {company.address.country}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
